import { NextFunction, Request, Response } from "express";
import Joi from "joi";

import { PROFILE_APPROVAL_STATUS } from "../models/constants/model-constants";
import { phoneJoiString } from "./phone-validation";

// Subject schema
const subjectSchema = Joi.object({
  name: Joi.string().required().trim().messages({
    "string.empty": "Subject name is required",
    "any.required": "Subject name is required",
  }),
  qualificationLevel: Joi.string().trim().optional().allow(""),
  yearsExperience: Joi.number().min(0).optional(),
  certifications: Joi.array().items(Joi.string().trim()).optional(),
});

// Education schema
const educationSchema = Joi.object({
  degree: Joi.string().required().trim().messages({
    "string.empty": "Degree is required",
    "any.required": "Degree is required",
  }),
  field: Joi.string().required().trim().messages({
    "string.empty": "Field of study is required",
    "any.required": "Field of study is required",
  }),
  institution: Joi.string().required().trim().messages({
    "string.empty": "Institution is required",
    "any.required": "Institution is required",
  }),
  graduationYear: Joi.number()
    .integer()
    .min(1950)
    .max(new Date().getFullYear())
    .required()
    .messages({
      "number.min": "Graduation year must be after 1950",
      "number.max": "Graduation year cannot be in the future",
      "any.required": "Graduation year is required",
    }),
  gpa: Joi.number().min(0).max(4).optional(),
});

// Experience schema
const experienceSchema = Joi.object({
  position: Joi.string().required().trim().messages({
    "string.empty": "Position is required",
    "any.required": "Position is required",
  }),
  institution: Joi.string().required().trim().messages({
    "string.empty": "Institution is required",
    "any.required": "Institution is required",
  }),
  startDate: Joi.date().required().messages({
    "any.required": "Start date is required",
  }),
  endDate: Joi.date().optional().allow(null),
  responsibilities: Joi.string().trim().optional().allow(""),
  isCurrent: Joi.boolean().optional(),
});

// Emergency contact schema
const emergencyContactSchema = Joi.object({
  name: Joi.string().required().trim().messages({
    "string.empty": "Emergency contact name is required",
    "any.required": "Emergency contact name is required",
  }),
  relationship: Joi.string().required().trim().messages({
    "string.empty": "Relationship is required",
    "any.required": "Relationship is required",
  }),
  phone: phoneJoiString.required().messages({
    "any.required": "Emergency contact phone is required",
  }),
  email: Joi.string().email().optional().allow(""),
});

// Preferences schema
const preferencesSchema = Joi.object({
  maxClassSize: Joi.number().integer().min(1).max(100).optional(),
  preferredSubjects: Joi.array().items(Joi.string().trim()).optional(),
  availableDays: Joi.array()
    .items(
      Joi.string()
        .valid(
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        )
        .insensitive(),
    )
    .optional(),
  notifications: Joi.object({
    email: Joi.boolean().optional(),
    push: Joi.boolean().optional(),
    sms: Joi.boolean().optional(),
  }).optional(),
});

// Update teacher validation
export const updateTeacherValidation = Joi.object({
  fullName: Joi.string().trim().max(100).optional(),
  dateOfBirth: Joi.date().optional(),
  gender: Joi.string()
    .valid("male", "female", "other")
    .insensitive()
    .optional(),
  phone: phoneJoiString.optional(),
  address: Joi.object({
    street: Joi.string().trim().optional().allow(""),
    city: Joi.string().trim().optional().allow(""),
    state: Joi.string().trim().optional().allow(""),
    country: Joi.string().trim().optional().allow(""),
    postalCode: Joi.string().trim().optional().allow(""),
  }).optional(),
  bio: Joi.string().trim().max(500).optional().allow(""),
  status: Joi.string()
    .valid("active", "on_leave", "terminated")
    .insensitive()
    .optional(),
  hireDate: Joi.date().optional(),
  subjects: Joi.array().items(subjectSchema).optional(),
  education: Joi.array().items(educationSchema).optional(),
  experience: Joi.array().items(experienceSchema).optional(),
  emergencyContact: emergencyContactSchema.optional(),
  preferences: preferencesSchema.optional(),
});

// Reject teacher validation
export const rejectTeacherValidation = Joi.object({
  reason: Joi.string().required().trim().min(10).max(500).messages({
    "string.empty": "Rejection reason is required",
    "string.min": "Rejection reason must be at least 10 characters",
    "string.max": "Rejection reason cannot exceed 500 characters",
    "any.required": "Rejection reason is required",
  }),
});

// Unified teacher status change validation
export const changeTeacherStatusValidation = Joi.object({
  status: Joi.string().valid("approve", "reject").required().messages({
    "any.only": "Status must be either 'approve' or 'reject'",
    "any.required": "Status is required",
  }),
  reason: Joi.string().trim().min(10).max(500).optional().messages({
    "string.min": "Rejection reason must be at least 10 characters",
    "string.max": "Rejection reason cannot exceed 500 characters",
  }),
});

// Terminate teacher validation
export const terminateTeacherValidation = Joi.object({
  reason: Joi.string().required().trim().min(10).max(500).messages({
    "string.empty": "Termination reason is required",
    "string.min": "Termination reason must be at least 10 characters",
    "string.max": "Termination reason cannot exceed 500 characters",
    "any.required": "Termination reason is required",
  }),
});

// Add subject validation
export const addSubjectValidation = subjectSchema;

// Performance metrics validation
export const performanceMetricsValidation = Joi.object({
  totalClasses: Joi.number().integer().min(0).optional(),
  totalStudents: Joi.number().integer().min(0).optional(),
  averageRating: Joi.number().min(0).max(5).optional(),
  completedAssignments: Joi.number().integer().min(0).optional(),
  attendanceRate: Joi.number().min(0).max(100).optional(),
});

// Teacher filtering validation
export const teacherFilterValidation = Joi.object({
  approvalStatus: Joi.string()
    .valid(...Object.values(PROFILE_APPROVAL_STATUS))
    .optional()
    .messages({
      "any.only": `Approval status must be one of: ${Object.values(PROFILE_APPROVAL_STATUS).join(", ")}`,
    }),
  status: Joi.string()
    .valid("active", "inactive", "on_leave", "terminated")
    .optional()
    .messages({
      "any.only":
        "Status must be one of: active, inactive, on_leave, terminated",
    }),
  subject: Joi.string().trim().optional().allow(""),
  search: Joi.string().trim().optional().allow(""),
  page: Joi.number().integer().min(1).optional().messages({
    "number.min": "Page must be at least 1",
  }),
  limit: Joi.number().integer().min(1).max(100).optional().messages({
    "number.min": "Limit must be at least 1",
    "number.max": "Limit cannot exceed 100",
  }),
  sortBy: Joi.string()
    .valid("createdAt", "fullName", "employeeId", "approvalStatus", "status")
    .optional()
    .messages({
      "any.only":
        "Sort field must be one of: createdAt, fullName, employeeId, approvalStatus, status",
    }),
  sortOrder: Joi.string().valid("asc", "desc").optional().messages({
    "any.only": "Sort order must be either 'asc' or 'desc'",
  }),
  dateFrom: Joi.date().optional().messages({
    "date.base": "Date from must be a valid date",
  }),
  dateTo: Joi.date().optional().messages({
    "date.base": "Date to must be a valid date",
  }),
  hasDocuments: Joi.boolean().optional(),
  isEmailVerified: Joi.boolean().optional(),
});

// Middleware to validate update teacher request
export const validateUpdateTeacher = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const { error } = updateTeacherValidation.validate(req.body, {
    abortEarly: false,
  });

  if (error) {
    res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details.map(detail => detail.message).join(", "),
    });
    return;
  }

  next();
};

// Middleware to validate change teacher status request
export const validateChangeTeacherStatus = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const { error } = changeTeacherStatusValidation.validate(req.body, {
    abortEarly: false,
  });

  if (error) {
    res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details.map(detail => detail.message).join(", "),
    });
    return;
  }

  next();
};

// Middleware to validate reject teacher request
export const validateRejectTeacher = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const { error } = rejectTeacherValidation.validate(req.body, {
    abortEarly: false,
  });

  if (error) {
    res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details.map(detail => detail.message).join(", "),
    });
    return;
  }

  next();
};

// Middleware to validate terminate teacher request
export const validateTerminateTeacher = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const { error } = terminateTeacherValidation.validate(req.body, {
    abortEarly: false,
  });

  if (error) {
    res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details.map(detail => detail.message).join(", "),
    });
    return;
  }

  next();
};

// Middleware to validate add subject request
export const validateAddSubject = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const { error } = addSubjectValidation.validate(req.body, {
    abortEarly: false,
  });

  if (error) {
    res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details.map(detail => detail.message).join(", "),
    });
    return;
  }

  next();
};

// Middleware to validate performance metrics request
export const validatePerformanceMetrics = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const { error } = performanceMetricsValidation.validate(req.body, {
    abortEarly: false,
  });

  if (error) {
    res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details.map(detail => detail.message).join(", "),
    });
    return;
  }

  next();
};

// Middleware to validate teacher filter request
export const validateTeacherFilter = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const { error } = teacherFilterValidation.validate(req.query, {
    abortEarly: false,
  });

  if (error) {
    res.status(400).json({
      success: false,
      message: "Validation failed",
      error: error.details.map(detail => detail.message).join(", "),
    });
    return;
  }

  next();
};
